import { Col, Row } from 'antd'
import { useTranslation } from 'react-i18next'

import { settingsImage } from 'assets/images'
import {
  ConfirmationModal,
  ListCard,
  ProductionTypeSelect,
  DeliveryConditionSelect,
} from 'components'
import { useUser, usePermissions } from 'hooks'
import { ActiveCompanyContext } from 'contexts'

import { Layout } from '../../components'
import { useFieldsSettings, useSeasonsSettings } from './hooks'

export const Settings: React.FC = () => {
  const { activeCompanyId } = ActiveCompanyContext.useActiveCompanyContext()

  const user = useUser()
  const { permissions } = usePermissions()
  const { t } = useTranslation('settings')

  const {
    showDeleteFieldModal,
    fields,
    loadingFields,
    deleteFieldLoading,
    lotsToDeleteAndSeasonsQty,
    onAddField,
    onEditField,
    onDeleteField,
    onConfirmDeleteField,
    onCancelDeleteField,
    updateField,
  } = useFieldsSettings()

  const {
    selectedSeasonToDelete,
    showDeleteSeasonModal,
    seasons,
    loadingSeasons,
    deleteSeasonLoading,
    onAddSeason,
    onEditSeason,
    onDeleteSeason,
    onConfirmDeleteSeason,
    onCancelDeleteSeason,
  } = useSeasonsSettings()

  const isAnOrganizationCompany = user?.companiesRoles?.find(
    company => company.company.id === activeCompanyId,
  )?.company.organization

  return (
    <Layout
      headerTitle={t('title', { context: permissions.fieldEntity && 'withFieldEntity' })}
      headerImage={settingsImage}
    >
      <Row gutter={[24, 24]}>
        {permissions.fieldEntity && (
          <Col xl={12} flex={1}>
            <Row>
              <ListCard
                maxWidth="850px"
                data={fields ?? []}
                title={t('cards.fields.title')}
                description={t('cards.fields.description')}
                header={
                  <Row>
                    <Col span={8}>{t('cards.fields.header')}</Col>
                    <Col span={12}>
                      <Row gutter={8}>
                        <Col span={isAnOrganizationCompany ? 12 : 16}>
                          {t('cards.fields.productionType')}
                        </Col>
                        {isAnOrganizationCompany && (
                          <Col span={12}>{t('cards.fields.deliveryCondition')}</Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                }
                buttonDescription={t('cards.fields.buttonText')}
                loading={loadingFields}
                onClick={onAddField}
                onEdit={onEditField}
                onDelete={onDeleteField}
                renderItem={item => (
                  <>
                    <Col span={8}>{item.name}</Col>
                    <Col span={12}>
                      <Row gutter={8}>
                        <Col span={isAnOrganizationCompany ? 12 : 16}>
                          <ProductionTypeSelect
                            value={item.productionType}
                            onChange={productionType => {
                              updateField({
                                variables: { id: item.id, updateFieldDTO: { productionType } },
                              })
                            }}
                          />
                        </Col>
                        {isAnOrganizationCompany && (
                          <Col span={12}>
                            <DeliveryConditionSelect
                              value={item.deliveryCondition}
                              onChange={deliveryCondition => {
                                updateField({
                                  variables: {
                                    id: item.id,
                                    updateFieldDTO: {
                                      deliveryCondition,
                                    },
                                  },
                                })
                              }}
                            />
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </>
                )}
                isAdminUser={permissions.isCompanyAdminUser}
              />
            </Row>
          </Col>
        )}
        <Col xl={12} flex={1}>
          <Row>
            <ListCard
              maxWidth="850px"
              data={seasons ?? []}
              title={
                isAnOrganizationCompany ? t('cards.seasons.titleClient') : t('cards.seasons.title')
              }
              description={
                isAnOrganizationCompany
                  ? t('cards.seasons.descriptionClient')
                  : t('cards.seasons.description')
              }
              header={t('cards.seasons.header')}
              buttonDescription={t('cards.seasons.buttonText')}
              disabledTooltipText={t('cards.seasons.disabledTooltipText')}
              loading={loadingSeasons}
              onClick={onAddSeason}
              onEdit={onEditSeason}
              onDelete={onDeleteSeason}
              renderItem={item => <Col>{item.name}</Col>}
              isAdminUser={permissions.isCompanyAdminUser}
              disabledButton={!permissions.seasonCreation}
            />
          </Row>
        </Col>
      </Row>
      {showDeleteFieldModal && (
        <ConfirmationModal
          description={t('cards.fields.deleteConfirmationText', {
            count: lotsToDeleteAndSeasonsQty.lots,
            seasons: lotsToDeleteAndSeasonsQty.seasons,
          })}
          onConfirm={onConfirmDeleteField}
          onCancel={onCancelDeleteField}
          loading={deleteFieldLoading}
        />
      )}
      {showDeleteSeasonModal && (
        <ConfirmationModal
          description={t('cards.seasons.deleteConfirmationText', {
            seasonName: selectedSeasonToDelete!.name,
          })}
          onConfirm={onConfirmDeleteSeason}
          onCancel={onCancelDeleteSeason}
          loading={deleteSeasonLoading}
        />
      )}
    </Layout>
  )
}
